import React, { useState, useRef } from 'react';
import { Image as ImageIcon, Bold, Italic, Code, List, ListOrdered, Link as LinkIcon, Eye, Edit2 } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MentionTextarea, mentionMarkdownComponents } from './Mentions';
import './MarkdownEditor.css';

function MarkdownEditor({ value, onChange, placeholder, members }) {
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);

  const insertText = (before, after = '', defaultText = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || defaultText;
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);

    onChange(newText);

    // Set cursor position
    setTimeout(() => {
      const newPos = start + before.length + selectedText.length;
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleBold = () => insertText('**', '**', 'bold text');
  const handleItalic = () => insertText('*', '*', 'italic text');
  const handleCode = () => insertText('`', '`', 'code');
  const handleCodeBlock = () => insertText('\n```\n', '\n```\n', 'code block');
  const handleBulletList = () => insertText('\n- ', '', 'list item');
  const handleNumberedList = () => insertText('\n1. ', '', 'list item');
  const handleLink = () => insertText('[', '](url)', 'link text');
  const handleHeading = (level) => insertText('\n' + '#'.repeat(level) + ' ', '', 'Heading');

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await axios.post('/folios/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const textarea = textareaRef.current;
      const cursorPos = textarea?.selectionStart || value.length;
      const imageMarkdown = `\n![${file.name}](${response.data.url})\n`;
      const newContent = value.substring(0, cursorPos) + imageMarkdown + value.substring(cursorPos);

      onChange(newContent);

      setTimeout(() => {
        textarea?.focus();
        textarea?.setSelectionRange(cursorPos + imageMarkdown.length, cursorPos + imageMarkdown.length);
      }, 0);
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="markdown-editor">
      <div className="markdown-toolbar">
        <div className="toolbar-group">
          <button onClick={handleBold} className="toolbar-btn" title="Bold">
            <Bold size={16} />
          </button>
          <button onClick={handleItalic} className="toolbar-btn" title="Italic">
            <Italic size={16} />
          </button>
          <button onClick={handleCode} className="toolbar-btn" title="Inline code">
            <Code size={16} />
          </button>
        </div>

        <div className="toolbar-group">
          <button onClick={() => handleHeading(1)} className="toolbar-btn" title="Heading 1">
            H1
          </button>
          <button onClick={() => handleHeading(2)} className="toolbar-btn" title="Heading 2">
            H2
          </button>
          <button onClick={() => handleHeading(3)} className="toolbar-btn" title="Heading 3">
            H3
          </button>
        </div>

        <div className="toolbar-group">
          <button onClick={handleBulletList} className="toolbar-btn" title="Bullet list">
            <List size={16} />
          </button>
          <button onClick={handleNumberedList} className="toolbar-btn" title="Numbered list">
            <ListOrdered size={16} />
          </button>
        </div>

        <div className="toolbar-group">
          <button onClick={handleLink} className="toolbar-btn" title="Link">
            <LinkIcon size={16} />
          </button>
          <label className="toolbar-btn" title="Upload image">
            <ImageIcon size={16} />
            {uploading && <span className="uploading-spinner"></span>}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </label>
        </div>

        <div className="toolbar-group toolbar-group-right">
          <button
            onClick={() => setPreview(!preview)}
            className={`toolbar-btn ${preview ? 'active' : ''}`}
            title={preview ? 'Edit mode' : 'Preview mode'}
          >
            {preview ? <Edit2 size={16} /> : <Eye size={16} />}
            {preview ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>

      <div className="markdown-editor-content">
        {preview ? (
          <div className="markdown-preview">
            {value ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mentionMarkdownComponents}>
                {value}
              </ReactMarkdown>
            ) : (
              <div className="preview-empty">
                <p>Nothing to preview. Switch to edit mode to write content.</p>
              </div>
            )}
          </div>
        ) : (
          <MentionTextarea
            inputRef={textareaRef}
            className="markdown-textarea"
            value={value}
            onChange={onChange}
            members={members}
            placeholder={placeholder || "Write your content in markdown... Type @ to mention a team member."}
          />
        )}
      </div>
    </div>
  );
}

export default MarkdownEditor;
